import { useCallback, useEffect, useMemo, useState } from 'react';
import type { SyncConfigurationModalState } from '../../model/syncConfigurationModal.model';
import type { KebabMenuOption } from '@ingenico/react-section-utils';
import { useSyncConfigurationToSingleDevice } from '../../hooks/useSyncConfigurationToDevice.hook';
import { LogicalDeviceSyncConfigurationModalVariant } from '@ingenico/type-mfe-contracts/domains';
import { useTranslation } from 'react-i18next';
import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import { SyncConfigurationEntityType } from '../sync-configuration-page-content/syncConfigurationPageContent.model';
import SyncIcon from '@mui/icons-material/Sync';

export interface UseSyncConfigurationButtonVMProps {
  readonly selectedEntity?: SyncConfigurationEntityType | null;
  readonly id?: string | string[];
}

export function useSyncConfigurationButtonVM({ selectedEntity, id }: UseSyncConfigurationButtonVMProps) {
  const { t } = useTranslation([ALL_LNGS_NAMESPACES.COMMON]);

  const [kebabOptions, setKebabOptions] = useState<KebabMenuOption[]>([{ label: '' }]);

  const setKebabMenuOptions = useCallback(
    (state: SyncConfigurationModalState) => setKebabOptions(state?.menuOption ? [state?.menuOption] : []),
    [],
  );

  const selectedLogicalDeviceIds = useMemo(() => (Array.isArray(id) ? id : id ? [id] : []), [id]);

  const { syncConfigurationModalComponent, handleSyncConfiguration } = useSyncConfigurationToSingleDevice({
    logicalDeviceIds: selectedLogicalDeviceIds,
    variant:
      selectedLogicalDeviceIds.length <= 1
        ? LogicalDeviceSyncConfigurationModalVariant.SingleDevice
        : LogicalDeviceSyncConfigurationModalVariant.MultipleDevices,
  });

  useEffect(() => {
    if (selectedEntity === SyncConfigurationEntityType.Device) {
      setKebabMenuOptions({
        menuOption: {
          disabled: false,
          icon: <SyncIcon />,
          label: t('button.syncConfigurationOpenModal', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE }),
          onClick: handleSyncConfiguration,
          dataTestId: 'li-sync-configuration',
        },
      });
    }
  }, [handleSyncConfiguration, selectedEntity, setKebabMenuOptions, t]);

  const shouldDisplayKebabMenu = useMemo(() => {
    return selectedEntity === SyncConfigurationEntityType.Device
      ? !Array.isArray(id) || (Array.isArray(id) && id.length <= 1)
      : true;
  }, [selectedEntity, id]);

  return {
    kebabOptions,
    setKebabMenuOptions,
    syncConfigurationModalComponent,
    handleSyncConfiguration,
    shouldDisplayKebabMenu,
  };
}
